import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MessageCircle, Zap, Shield, Sparkles } from 'lucide-react';

// AI Model Icon Components
const ChatGPTIcon = () => (
  <svg viewBox="0 0 24 24" className="w-full h-full">
    <path fill="currentColor" d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
  </svg>
);

const GeminiIcon = () => (
  <svg viewBox="0 0 24 24" className="w-full h-full">
    <path fill="currentColor" d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
  </svg>
);

const ClaudeIcon = () => (
  <svg viewBox="0 0 24 24" className="w-full h-full">
    <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
  </svg>
);

const DeepSeekIcon = () => (
  <svg viewBox="0 0 24 24" className="w-full h-full">
    <path fill="currentColor" d="M13 3l3.293 3.293-7 7 1.414 1.414 7-7L21 11V3z"/>
    <path fill="currentColor" d="M19 19H5V5h7l-2-2H5c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2h14c1.103 0 2-.897 2-2v-5l-2-2v7z"/>
  </svg>
);

const Home = () => {
  const { isAuthenticated } = useAuth();

  const aiModels = [
    {
      id: 'chatgpt',
      name: 'ChatGPT',
      description: 'OpenAI GPT-3.5 Turbo',
      icon: ChatGPTIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      id: 'gemini',
      name: 'Gemini',
      description: 'Google Gemini Pro',
      icon: GeminiIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      id: 'claude',
      name: 'Claude',
      description: 'Anthropic Claude 3 Haiku',
      icon: ClaudeIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      id: 'deepseek',
      name: 'DeepSeek',
      description: 'DeepSeek Chat',
      icon: DeepSeekIcon,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    }
  ];

  const features = [
    {
      icon: MessageCircle,
      title: 'Multiple AI Models',
      description: 'Chat with ChatGPT, Gemini, Claude, and DeepSeek all in one place.'
    },
    {
      icon: Zap,
      title: 'Token-Based System',
      description: 'Fair usage with a simple token system. Start with 1000 free tokens.'
    },
    {
      icon: Shield,
      title: 'Secure & Private',
      description: 'Your conversations are secure with JWT authentication and encryption.'
    },
    {
      icon: Sparkles,
      title: 'Modern Interface',
      description: 'Clean, responsive design that works perfectly on all devices.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Hero Section */}
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            AI Fusion Chat
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
            Experience the power of multiple AI models in one unified platform. 
            Chat with ChatGPT, Gemini, Claude, and DeepSeek seamlessly.
          </p>
          
          {!isAuthenticated ? (
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <Link
                to="/signup"
                className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-2.5 sm:py-3 w-full sm:w-auto"
              >
                Get Started Free
              </Link>
              <Link
                to="/login"
                className="btn-secondary text-base sm:text-lg px-6 sm:px-8 py-2.5 sm:py-3 w-full sm:w-auto"
              >
                Sign In
              </Link>
            </div>
          ) : (
            <Link
              to="/chat"
              className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-2.5 sm:py-3 inline-flex items-center justify-center w-full sm:w-auto max-w-xs mx-auto"
            >
              <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Start Chatting
            </Link>
          )}
        </div>

        {/* AI Models Showcase */}
        <div className="mb-12 sm:mb-16">
          <div className="text-center mb-8 sm:mb-12 px-4">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Powered by Leading AI Models
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Access the most advanced AI models from top companies, all in one unified platform
            </p>
          </div>
          
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 px-4 sm:px-0">
            {aiModels.map((model) => {
              const ModelIcon = model.icon;
              return (
                <div
                  key={model.id}
                  className={`${model.bgColor} ${model.borderColor} border-2 rounded-xl p-4 sm:p-6 text-center hover:shadow-lg transition-all duration-300 hover:scale-105 active:scale-95`}
                >
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 ${model.bgColor} rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-sm`}>
                    <div className={`w-7 h-7 sm:w-10 sm:h-10 ${model.color}`}>
                      <ModelIcon />
                    </div>
                  </div>
                  <h3 className={`text-lg sm:text-xl font-bold ${model.color} mb-1 sm:mb-2`}>
                    {model.name}
                  </h3>
                  <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                    {model.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Features Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-12 sm:mb-16 px-4 sm:px-0">
          {features.map((feature, index) => (
            <div key={index} className="card text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 md:p-12 mx-4 sm:mx-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                Why Choose AI Fusion Chat?
              </h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-600 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Access Multiple AI Models</h4>
                    <p className="text-gray-600 text-sm sm:text-base leading-relaxed">Compare responses from different AI models to get the best answers.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-600 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Fair Token System</h4>
                    <p className="text-gray-600 text-sm sm:text-base leading-relaxed">Simple pricing with 1 token per message. No hidden fees or subscriptions.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 sm:mt-1">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-600 rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">Secure & Reliable</h4>
                    <p className="text-gray-600 text-sm sm:text-base leading-relaxed">Enterprise-grade security with JWT authentication and data encryption.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-6 sm:p-8 text-white order-1 lg:order-2">
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Ready to get started?</h3>
              <p className="mb-4 sm:mb-6 opacity-90 text-sm sm:text-base leading-relaxed">
                Join thousands of users who are already experiencing the future of AI conversation.
              </p>
              {!isAuthenticated ? (
                <Link
                  to="/signup"
                  className="bg-white text-primary-600 font-semibold py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg hover:bg-gray-100 transition-colors inline-block text-sm sm:text-base w-full sm:w-auto text-center"
                >
                  Create Free Account
                </Link>
              ) : (
                <Link
                  to="/chat"
                  className="bg-white text-primary-600 font-semibold py-2.5 sm:py-3 px-5 sm:px-6 rounded-lg hover:bg-gray-100 transition-colors inline-block text-sm sm:text-base w-full sm:w-auto text-center"
                >
                  Go to Chat
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
